import type {
  CommonJob,
  CommonJob as Job,
} from "@repo/design-system/types/job";
import type { ComponentType } from "react";
import { env } from "../../setup/env";

export interface SubtemplateConfig<TComponentKeys extends string> {
  overrides?: Partial<Record<TComponentKeys, JobComponent>>;
  sectionBarItems?: {
    href: string;
    label: string;
    section: string;
  }[];
  sections: TComponentKeys[];
}

/**
 * A **content-only sub-variant** of a template. It has no own layout: it inherits
 * the layout (`config`) of the parent template it is nested under, so it is always
 * unambiguous which layout it gets. Resolved by `trigger` and identified by `name`
 * (the key used in {@link pickByVariant}).
 *
 * Mirrors the legacy `basedOn` idea (e.g. `azubi` → `azubiTechnisch`): a variation
 * of a specific template, not a standalone one.
 */
export interface SubtemplateVariant {
  name: string;
  /** Defaults to true. Set false to hide the variant in production. */
  showProd?: boolean;
  trigger: (job: Job) => boolean;
}

export interface SubtemplateCondition<TComponentKeys extends string> {
  config: SubtemplateConfig<TComponentKeys>;
  name: string;
  showProd: boolean;
  trigger: (job: Job) => boolean;
  /**
   * Content-only sub-variants that inherit THIS template's layout (`config`).
   * Define lightweight variations here — primarily just a name + trigger — so it
   * is visible in one place which extra templates exist and which layout they get.
   * Resolve their content with {@link pickByVariant} instead of scattering
   * `if (job…)` checks across components.
   *
   * @example
   * createSubtemplate({
   *   name: "azubi",
   *   trigger: (job) => job.data.entryLevel === "Auszubildende",
   *   showProd: true,
   *   config: { sections: [...] },
   *   variants: [
   *     { name: "azubiTechnisch", trigger: (job) => job.data.jobField === "Technik" },
   *   ],
   * })
   */
  variants?: SubtemplateVariant[];
}

export type JobComponent<TJob extends CommonJob = CommonJob> = ComponentType<{
  job: TJob;
  // biome-ignore lint/suspicious/noExplicitAny: <any gets inferred>
  config?: SubtemplateConfig<any>;
}>;

export interface ClientRegistryEntry<
  TClientUI,
  TComponents,
  // biome-ignore lint/suspicious/noExplicitAny: <any gets inferred>
  TSubtemplates extends SubtemplateCondition<any>,
> {
  components: TComponents;
  subtemplates: TSubtemplates[];
  ui: TClientUI;
}

export type ClientRegistry<
  // biome-ignore lint/suspicious/noExplicitAny: <any gets inferred>
  T extends Record<string, ClientRegistryEntry<any, any, any>>,
> = {
  [K in keyof T]: T[K];
};

export function createRegistry<
  // biome-ignore lint/suspicious/noExplicitAny: <any gets inferred>
  T extends Record<string, ClientRegistryEntry<any, any, any>>,
>(registry: T) {
  return registry;
}

export function createClientConfig<
  const TClientUI,
  const TComponents,
  // biome-ignore lint/suspicious/noExplicitAny: <any gets inferred>
  TSubtemplates extends SubtemplateCondition<any>,
>(config: ClientRegistryEntry<TClientUI, TComponents, TSubtemplates>) {
  return config;
}

export function createSubtemplate<TComponentKeys extends string>(
  subtemplate: SubtemplateCondition<TComponentKeys>
) {
  return subtemplate;
}

export function getClientRegistry<
  TClient extends string,
  // biome-ignore lint/suspicious/noExplicitAny: <gets inferred>
  TRegistry extends ClientRegistry<any>,
>(client: TClient, clientRegistry: TRegistry) {
  const registry = clientRegistry[client];
  if (!registry) {
    // Don't use env.CLIENT as fallback to avoid DYNAMIC_SERVER_USAGE
    // The client should already be validated before calling this function
    throw new Error(
      `Client "${client}" not found in registry. Available clients: ${Object.keys(clientRegistry).join(", ")}`
    );
  }
  return registry as TRegistry[TClient];
}

/**
 * Single source of truth for resolving a job to a template. Iterates top-level
 * conditions in order (most specific first, `default` last); within each, checks
 * nested {@link SubtemplateVariant}s FIRST (more specific) then the template's own
 * trigger. Whichever matches, the **layout is always the parent template's
 * `config`** — a nested variant only changes the resolved `name`.
 *
 * showProd-aware and error-safe (a throwing trigger is skipped) so the `default`
 * template can always win as fallback.
 */
export function resolveSubtemplate<TComponentKeys extends string>(
  subtemplates: SubtemplateCondition<TComponentKeys>[] | undefined,
  job: Job
): { config: SubtemplateConfig<TComponentKeys>; name: string } | undefined {
  if (!subtemplates) {
    return;
  }

  const isProduction = env.ENVIRONMENT === "production";
  const matches = (trigger: (job: Job) => boolean) => {
    try {
      return trigger(job);
    } catch {
      // A throwing trigger is treated as "no match" so fallbacks still apply.
      return false;
    }
  };

  for (const condition of subtemplates) {
    if (isProduction && !condition.showProd) {
      continue;
    }

    // Most specific first: a matching nested variant inherits this layout.
    for (const variant of condition.variants ?? []) {
      if (isProduction && variant.showProd === false) {
        continue;
      }
      if (matches(variant.trigger)) {
        return { config: condition.config, name: variant.name };
      }
    }

    if (matches(condition.trigger)) {
      return { config: condition.config, name: condition.name };
    }
  }

  // No matching condition found — caller falls back to defaults.
  return;
}

export function getSubtemplateConfig(
  // biome-ignore lint/suspicious/noExplicitAny: <gets inferred>
  subtemplates: SubtemplateCondition<any>[] | undefined,
  job: Job
) {
  return resolveSubtemplate(subtemplates, job)?.config;
}

/**
 * Resolve which template variant a job belongs to, by NAME, using the exact same
 * resolution as {@link getSubtemplateConfig} (so name and layout always agree).
 * Returns the matching nested-variant or template `name`, or `"default"` if none
 * match. Single source of truth for "which variant is this job?".
 */
export function resolveVariantName(
  // biome-ignore lint/suspicious/noExplicitAny: <gets inferred>
  subtemplates: SubtemplateCondition<any>[] | undefined,
  job: Job
): string {
  return resolveSubtemplate(subtemplates, job)?.name ?? "default";
}

/**
 * Pick a per-variant value for a job. Centralizes content that differs by
 * template variant (benefits, contact, steps, colors, …) into a single value
 * map keyed by variant name — the new-repo equivalent of the legacy
 * `getSubTemplateConfig`. The "which variant" logic lives once in the registry
 * triggers; components only supply the value map.
 *
 * @example
 * const benefits = pickByVariant(job, subtemplates, {
 *   default: BENEFITS_DEFAULT,
 *   servicetechniker: BENEFITS_SERVICETECHNIKER,
 * });
 */
export function pickByVariant<T>(
  job: Job,
  // biome-ignore lint/suspicious/noExplicitAny: <gets inferred>
  subtemplates: SubtemplateCondition<any>[] | undefined,
  map: { default: T } & Record<string, T>
): T {
  const name = resolveVariantName(subtemplates, job);
  return map[name] ?? map.default;
}
